'use strict';

// Session upload is three stages, summed across every finished trial:
// camera files downloaded off the cameras, those files uploaded to the server,
// and EvoWalk files uploaded to the server. EvoWalk download happens between
// trials and is not part of this bar.

function toCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

function firstCount(source, keys) {
  if (!source || typeof source !== 'object') return null;
  for (let i = 0; i < keys.length; i += 1) {
    const n = toCount(source[keys[i]]);
    if (n != null) return n;
  }
  return null;
}

function wholeCount(value) {
  const n = toCount(value);
  if (n == null) return 0;
  return Math.floor(n);
}

export function createSessionProgress(options) {
  const opts = options && typeof options === 'object' ? options : {};
  const trialCount = Math.max(1, wholeCount(opts.trialCount));
  const cameraCount = wholeCount(opts.cameraCount);
  const files = trialCount * cameraCount;
  return {
    trialCount,
    cameraCount,
    evowalkEnabled: !!opts.evowalkEnabled,
    cameraDownload: { done: 0, total: files },
    cameraUpload: { done: 0, total: files },
    evowalkUpload: { done: 0, total: 0, known: false },
    byTrial: {},
    activeTrialId: null,
  };
}

function cloneProgress(progress) {
  const byTrial = progress.byTrial || {};
  return {
    trialCount: progress.trialCount,
    cameraCount: progress.cameraCount,
    evowalkEnabled: !!progress.evowalkEnabled,
    cameraDownload: { ...progress.cameraDownload },
    cameraUpload: { ...(progress.cameraUpload || { done: 0, total: 0 }) },
    evowalkUpload: { ...(progress.evowalkUpload || { done: 0, total: 0, known: false }) },
    byTrial: Object.fromEntries(Object.entries(byTrial).map(([id, trial]) => [id, {
      cameraUpload: { ...trial.cameraUpload },
      evowalkUpload: { ...trial.evowalkUpload },
    }])),
    activeTrialId: progress.activeTrialId == null ? null : progress.activeTrialId,
  };
}

function readEvowalkUpload(data) {
  const nested = data.evowalk && typeof data.evowalk === 'object' && !Array.isArray(data.evowalk)
    ? data.evowalk
    : null;
  const done = firstCount(data, ['evowalk_uploaded', 'evo_uploaded'])
    ?? (nested ? firstCount(nested, ['uploaded']) : null);
  const total = firstCount(data, ['evowalk_total', 'evo_total'])
    ?? (nested ? firstCount(nested, ['total']) : null);
  return {
    done: done == null ? 0 : done,
    total: total == null ? 0 : total,
    known: done != null || total != null,
  };
}

function parseTrialEntry(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const rawId = data.trial_id != null ? data.trial_id : data.trialId;
  return {
    trialId: rawId == null || rawId === '' ? null : rawId,
    cameras: {
      done: firstCount(data, ['uploaded']),
      total: firstCount(data, ['total']),
    },
    evowalk: readEvowalkUpload(data),
  };
}

function listUploadEntries(uploadData) {
  if (Array.isArray(uploadData)) {
    return { entries: uploadData.map(parseTrialEntry).filter(Boolean), fullSnapshot: true };
  }
  if (uploadData && typeof uploadData === 'object' && Array.isArray(uploadData.trials)) {
    return { entries: uploadData.trials.map(parseTrialEntry).filter(Boolean), fullSnapshot: true };
  }
  const entry = parseTrialEntry(uploadData);
  return { entries: entry ? [entry] : [], fullSnapshot: false };
}

function sessionCameraFiles(progress) {
  const trials = wholeCount(progress && progress.trialCount);
  const cams = wholeCount(progress && progress.cameraCount);
  if (trials > 0 && cams > 0) return trials * cams;
  return 0;
}

function completeTrial(trial) {
  if (!trial) return;
  // EvoWalk JSON is downloaded and uploaded after each trial, so leaving a
  // trial means that JSON is done. Camera videos stay on the cameras until
  // the end of the session, so do not invent a finished camera upload here.
  if (trial.evowalkUpload.known && trial.evowalkUpload.total > 0) {
    trial.evowalkUpload.done = Math.max(trial.evowalkUpload.done, trial.evowalkUpload.total);
  }
}

function applyEntry(next, entry, fullSnapshot) {
  // Camera total is the rig size from /api/total-cameras. A smaller `total`
  // on the upload poll is the EvoWalk JSON that already went up after the trial.
  const reportedCameras = entry.cameras.total != null && entry.cameras.total > 0
    ? entry.cameras.total
    : 0;
  const perTrialCameras = next.cameraCount > 0
    ? Math.max(next.cameraCount, reportedCameras)
    : reportedCameras;
  const cameraDone = entry.cameras.done == null ? 0 : entry.cameras.done;

  if (entry.trialId == null) {
    if (entry.cameras.total != null && entry.cameras.total > next.cameraUpload.total) {
      next.cameraUpload.total = entry.cameras.total;
    }
    const cap = next.cameraUpload.total || cameraDone;
    next.cameraUpload.done = Math.min(cameraDone, cap);
    if (next.evowalkEnabled && entry.evowalk.known) {
      const evoTotal = entry.evowalk.total;
      next.evowalkUpload = {
        done: Math.min(entry.evowalk.done, evoTotal || entry.evowalk.done),
        total: evoTotal,
        known: true,
      };
    }
    return;
  }

  const id = String(entry.trialId);
  if (!fullSnapshot && next.activeTrialId != null && String(next.activeTrialId) !== id) {
    completeTrial(next.byTrial[String(next.activeTrialId)]);
  }
  next.byTrial[id] = {
    cameraUpload: {
      done: perTrialCameras > 0 ? Math.min(cameraDone, perTrialCameras) : cameraDone,
      total: perTrialCameras,
    },
    evowalkUpload: next.evowalkEnabled
      ? { done: entry.evowalk.done, total: entry.evowalk.total, known: entry.evowalk.known }
      : { done: 0, total: 0, known: false },
  };
  next.activeTrialId = entry.trialId;
}

function rollupTrials(next) {
  const ids = Object.keys(next.byTrial);
  if (!ids.length) return;
  const cameraDone = ids.reduce((sum, id) => sum + (next.byTrial[id].cameraUpload.done || 0), 0);
  const cameraTotal = ids.reduce((sum, id) => sum + (next.byTrial[id].cameraUpload.total || 0), 0);
  if (cameraTotal > next.cameraUpload.total) {
    next.cameraUpload.total = cameraTotal;
  }
  const cap = next.cameraUpload.total || cameraDone;
  next.cameraUpload.done = Math.min(cameraDone, cap);

  if (!next.evowalkEnabled) return;
  const known = ids.some((id) => next.byTrial[id].evowalkUpload.known);
  if (!known) return;
  const evoDone = ids.reduce((sum, id) => sum + (next.byTrial[id].evowalkUpload.done || 0), 0);
  const perTrial = ids.reduce((max, id) => {
    const total = next.byTrial[id].evowalkUpload.total || 0;
    return total > max ? total : max;
  }, 0);
  const sessionTotal = perTrial * next.trialCount;
  next.evowalkUpload = {
    done: Math.min(evoDone, sessionTotal || evoDone),
    total: sessionTotal,
    known: true,
  };
}

export function applyTransferPoll(progress, downloadData, uploadData) {
  const base = progress && progress.cameraDownload
    ? cloneProgress(progress)
    : createSessionProgress(progress);
  const downloaded = firstCount(downloadData, ['downloaded']);
  const downloadTotal = firstCount(downloadData, ['total']);
  const expectedCameras = sessionCameraFiles(base);
  if (expectedCameras > 0) {
    base.cameraDownload.total = Math.max(base.cameraDownload.total || 0, expectedCameras);
  }
  if (downloadTotal != null && downloadTotal > base.cameraDownload.total) {
    base.cameraDownload.total = downloadTotal;
  }
  if (downloaded != null) {
    const cap = base.cameraDownload.total > 0 ? base.cameraDownload.total : downloaded;
    base.cameraDownload.done = Math.min(downloaded, cap);
  }

  const { entries, fullSnapshot } = listUploadEntries(uploadData);
  entries.forEach((entry) => applyEntry(base, entry, fullSnapshot));
  rollupTrials(base);
  return base;
}

export function formatTransferLines(progress) {
  const source = progress && progress.cameraDownload ? progress : null;
  const download = source ? source.cameraDownload : { done: 0, total: 0 };
  const upload = source ? source.cameraUpload : { done: 0, total: 0 };
  const lines = [
    {
      key: 'cameraDownload',
      label: 'Camera download',
      done: download.done || 0,
      total: download.total || 0,
    },
    {
      key: 'cameraUpload',
      label: 'Camera upload',
      done: upload.done || 0,
      total: upload.total || 0,
    },
  ];
  if (source && source.evowalkEnabled) {
    const evo = source.evowalkUpload || { done: 0, total: 0, known: false };
    lines.push({
      key: 'evowalkUpload',
      label: 'EvoWalk upload',
      done: evo.known ? (evo.done || 0) : 0,
      total: evo.known ? (evo.total || 0) : 0,
      pending: !evo.known,
    });
  }
  return lines;
}

export function formatUploadProgress(progress) {
  return formatTransferLines(progress).map((line) => (
    line.pending ? `${line.label} …` : `${line.label} ${line.done}/${line.total}`
  )).join(' · ');
}

export function uploadPercent(progress) {
  const lines = formatTransferLines(progress).filter((line) => !line.pending && line.total > 0);
  const done = lines.reduce((sum, line) => sum + line.done, 0);
  const total = lines.reduce((sum, line) => sum + line.total, 0);
  if (!total) return 0;
  const percent = Math.round((done / total) * 100);
  if (percent < 0) return 0;
  if (percent > 100) return 100;
  return percent;
}

export function isUploadFinished(progress) {
  if (!progress || !progress.cameraDownload) return false;
  const download = progress.cameraDownload;
  const cameras = progress.cameraUpload || { done: 0, total: 0 };
  const expectedCameras = sessionCameraFiles(progress);
  const downloadDone = expectedCameras > 0
    ? download.done >= expectedCameras
    : download.total > 0 && download.done >= download.total;
  const camerasDone = expectedCameras > 0
    ? cameras.done >= expectedCameras
    : cameras.total > 0 && cameras.done >= cameras.total;
  if (!downloadDone || !camerasDone) return false;
  if (!progress.evowalkEnabled) return true;
  const evowalk = progress.evowalkUpload || { done: 0, total: 0, known: false };
  // EvoWalk JSON is uploaded after each trial, often before this button runs.
  // A finished EvoWalk count does not stand in for the end-of-session cameras.
  if (!evowalk.known || !evowalk.total) return true;
  return evowalk.done >= evowalk.total;
}

export function trialUploadRows(progress) {
  if (!progress || !progress.byTrial) return [];
  if (!(progress.trialCount > 1)) return [];
  return Object.keys(progress.byTrial).map((id) => {
    const trial = progress.byTrial[id];
    const known = !!(trial.evowalkUpload && trial.evowalkUpload.known);
    return {
      id,
      cameraDone: trial.cameraUpload.done || 0,
      cameraTotal: trial.cameraUpload.total || 0,
      evowalkDone: known ? (trial.evowalkUpload.done || 0) : null,
      evowalkTotal: known ? (trial.evowalkUpload.total || 0) : null,
    };
  });
}
